import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { AuthService } from "@core/services/auth.service";
import { catchError, Observable, switchMap, throwError } from "rxjs";

@Injectable()
export class RegisterInterceptor implements HttpInterceptor {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(this.withAuth(request)).pipe(
      catchError((error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
        // Auth endpoints handle their own 401s; retrying them here would loop.
        if (error.status !== 401 || this.isAuthEndpoint(request.url)) {
          return throwError(() => error);
        }

        // Access token likely expired: try one silent refresh (using the HttpOnly cookie),
        // then replay the original request with the new token.
        return this.authService.refresh$().pipe(
          switchMap(() => next.handle(this.withAuth(request))),
          catchError((): Observable<HttpEvent<unknown>> => {
            this.authService.clearSession();
            void this.router.navigate(["/login"]);

            return throwError(() => error);
          })
        );
      })
    );
  }

  private withAuth(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const accessToken = this.authService.authToken;

    return accessToken
      ? request.clone({
          setHeaders: { Authorization: `Bearer ${accessToken}` },
        })
      : request;
  }

  private isAuthEndpoint(url: string): boolean {
    // Every auth endpoint handles its own 401 — the login/refresh/logout endpoints and the anonymous
    // two-factor enrolment/challenge endpoints (a wrong 2FA code legitimately returns 401 and must
    // NOT trigger a silent refresh + logout). Only /api/auth/me is a normal authenticated call that
    // should be retried after a silent refresh when the access token has expired.
    return url.includes("/api/auth/") && !url.includes("/api/auth/me");
  }
}
